import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Spade,
  LayoutDashboard,
  Image,
  Calendar,
  Trophy,
  Award,
  Gift,
  Home,
  Phone,
  LogOut,
  Menu,
  X,
  MessageSquare,
  PlaySquare,
  Radio,
  Settings,
  ExternalLink,
} from "lucide-react";

// Import Admin Managers
import GalleryManager from "../components/admin/GalleryManager";
import EventsManager from "../components/admin/EventsManager";
import LeaderboardManager from "../components/admin/LeaderboardManager";
import HallOfFameManager from "../components/admin/HallOfFameManager";
import PromotionsManager from "../components/admin/PromotionsManager";
import HomepageManager from "../components/admin/HomepageManager";
import ContactManager from "../components/admin/ContactManager";
import MessagesManager from "../components/admin/MessagesManager";
import LiveBoardManager from "../components/admin/LiveBoardManager";
import LiveStreamManager from "../components/admin/LiveStreamManager";
import DashboardHome from "../components/admin/DashboardHome";
import SettingsManager from "../components/admin/SettingsManager";

const navItems = [
  { path: "", icon: LayoutDashboard, label: "Dashboard" },
  { path: "homepage", icon: Home, label: "Homepage" },
  { path: "events", icon: Calendar, label: "Events" },
  { path: "gallery", icon: Image, label: "Gallery" },
  { path: "leaderboard", icon: Trophy, label: "Leaderboard" },
  { path: "hall-of-fame", icon: Award, label: "Hall of Fame" },
  { path: "promotions", icon: Gift, label: "Promotions" },
  { path: "live-stream", icon: Radio, label: "Live Stream" },
  { path: "live-board", icon: PlaySquare, label: "Live Board" },
  { path: "contact", icon: Phone, label: "Contact Info" },
  { path: "messages", icon: MessageSquare, label: "Messages" },
  { path: "settings", icon: Settings, label: "Settings" },
];

export default function AdminDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout, email } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Force content refresh when navigating back to admin
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const isActive = (path) => {
    const currentPath = location.pathname.replace("/admin/dashboard", "").replace("/", "");
    return currentPath === path;
  };

  return (
    <div className="min-h-screen bg-casino-black" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-black/95 border-b border-white/10 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Spade className="w-6 h-6 text-gold" />
          <span className="font-heading text-white">SMSC Admin</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-white hover:text-gold transition-colors"
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-black border-r border-white/10 z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
          <Spade className="w-8 h-8 text-gold" />
          <span className="font-heading text-xl text-white">Admin Panel</span>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-180px)]">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={`/admin/dashboard/${item.path}`}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive(item.path)
                  ? "bg-gold/10 text-gold"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.path || 'dashboard'}`}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-black">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-gold hover:bg-gold/10 transition-all mb-3 text-sm font-medium"
            data-testid="view-website-btn"
          >
            <ExternalLink className="w-4 h-4 shrink-0" />
            View Website
          </Link>
          <div className="border-t border-white/10 pt-3">
            <div className="text-white/40 text-xs mb-2 truncate">{email}</div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/60 hover:text-casino-red transition-colors text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/homepage" element={<HomepageManager />} />
            <Route path="/events" element={<EventsManager />} />
            <Route path="/gallery" element={<GalleryManager />} />
            <Route path="/leaderboard" element={<LeaderboardManager />} />
            <Route path="/hall-of-fame" element={<HallOfFameManager />} />
            <Route path="/promotions" element={<PromotionsManager />} />
            <Route path="/live-stream" element={<LiveStreamManager />} />
            <Route path="/live-board" element={<LiveBoardManager />} />
            <Route path="/contact" element={<ContactManager />} />
            <Route path="/messages" element={<MessagesManager />} />
            <Route path="/settings" element={<SettingsManager />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
